import { Link } from "react-router-dom";
import { SITE, CITIES, CATEGORIES } from "@/lib/seoData";
import SocialLinks from "@/components/SocialLinks";

/**
 * Shared site footer with full compliance, contact, and SEO link mass.
 */
export default function SiteFooter() {
  return (
    <footer className="bg-[#050505] text-zinc-400 border-t border-zinc-900">
      <div className="max-w-7xl mx-auto px-6 pt-14 pb-[calc(6rem+env(safe-area-inset-bottom))] md:pb-14">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-x-6 gap-y-10 md:gap-10">
          {/* Company */}
          <div className="col-span-2">
            <h2 className="text-white text-lg font-semibold mb-4">{SITE.name}</h2>
            <p className="text-sm leading-6">
              {SITE.address.street}<br />
              {SITE.address.locality} {SITE.address.region} {SITE.address.postcode}
            </p>
            <p className="text-sm leading-6 mt-4">
              <a href={`tel:${SITE.phone}`} className="hover:text-white">{SITE.phoneDisplay}</a><br />
              <a href={`mailto:${SITE.email}`} className="hover:text-white">{SITE.email}</a>
            </p>
            <p className="text-xs text-zinc-600 mt-4">
              ACL #{SITE.acl} · AFCA #{SITE.afca} · MFAA #{SITE.mfaa}
            </p>
            <div className="mt-5">
              <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-600 mb-2.5">Follow</p>
              <SocialLinks size="md" />
            </div>
          </div>

          {/* Finance */}
          <div>
            <h3 className="text-white text-sm font-semibold mb-4">Finance</h3>
            <ul className="space-y-2 text-sm">
              {CATEGORIES.filter((c) => !c.pillar).slice(0, 6).map((c) => (
                <li key={c.slug}>
                  <Link to={`/finance/${c.slug}`} className="hover:text-white">{c.title}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* More finance */}
          <div>
            <h3 className="text-white text-sm font-semibold mb-4">More</h3>
            <ul className="space-y-2 text-sm">
              {CATEGORIES.filter((c) => !c.pillar).slice(6).map((c) => (
                <li key={c.slug}>
                  <Link to={`/finance/${c.slug}`} className="hover:text-white">{c.title}</Link>
                </li>
              ))}
              <li><Link to="/insights" className="hover:text-white">Insights</Link></li>
            </ul>
          </div>

          {/* Australia-wide */}
          <div className="col-span-2 md:col-span-1">
            <h3 className="text-white text-sm font-semibold mb-4">Australia-wide</h3>
            <p className="text-sm leading-6 text-zinc-400">
              Finance search across Australia — banks, non-banks and private capital.
            </p>
            <p className="text-sm leading-7 text-zinc-500 mt-3">
              <Link to="/locations/melbourne-cbd" className="hover:text-white">Melbourne</Link>
              {" · "}
              <Link to="/locations/sydney" className="hover:text-white">Sydney</Link>
              {" · "}
              <Link to="/locations/brisbane" className="hover:text-white">Brisbane</Link>
              {" · "}
              <Link to="/locations/perth" className="hover:text-white">Perth</Link>
              {" · "}
              <Link to="/locations/adelaide" className="hover:text-white">Adelaide</Link>
              {" · "}
              <span>Hobart</span>
            </p>
          </div>

          {/* Legal */}
          <div className="col-span-2 md:col-span-1">
            <h3 className="text-white text-sm font-semibold mb-4">Company</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/about" className="hover:text-white" data-testid="footer-about-link">About Us</Link></li>
              <li><Link to="/credit-guide" className="hover:text-white">Credit Guide</Link></li>
              <li><Link to="/privacy" className="hover:text-white">Privacy</Link></li>
              <li><Link to="/complaints" className="hover:text-white">Complaints</Link></li>
            </ul>
          </div>
        </div>

        {/* Secondary location row — desktop internal linking (hidden on mobile presentation) */}
        <div className="hidden md:block mt-10 pt-8 border-t border-zinc-900">
          <p className="text-[11px] text-zinc-600 mb-3 uppercase tracking-widest">Servicing Australia-wide</p>
          <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs text-zinc-500">
            {CITIES.map((c) => (
              <Link key={c.slug} to={`/locations/${c.slug}`} className="hover:text-white">
                {c.name} finance specialist
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-zinc-900 text-xs text-zinc-600 flex flex-col sm:flex-row gap-2 justify-between">
          <span>© {new Date().getFullYear()} {SITE.name}. All rights reserved.</span>
          <span>
            Information on this site is general in nature. We may receive commissions from lenders we introduce you to —
            see our <Link to="/credit-guide" className="underline hover:text-white">Credit Guide</Link>.
          </span>
        </div>
      </div>
    </footer>
  );
}
